<template>
  <div>
    <TStickyHeader :show-back-button="true" @back="navigateTo('/course')">
      <TH1>{{ pageTitle }}</TH1>
      <LegacyApiCourseBadge
        v-if="data && data.course"
        :course="data.course"
        class="ml-2"
      />
    </TStickyHeader>
    <CourseTabNavigation :course-id="courseId" :page="'settings'" />
    <div class="mx-4 max-w-3xl p-4">
      <div v-if="fetching">
        <TSettingsBlockLoading />
        <TSettingsBlockLoading />
        <TSettingsBlockLoading />
      </div>
      <div v-else-if="error">
        <TCalloutErrorGeneric />
      </div>
      <div v-else>
        <CourseSettingsDetails
          v-if="data.course && data.course.permissions.canEditCourseSettings"
          :course="data.course"
          force-open
          icon="settings"
        />

        <TSettingsBlock
          :title="$t('course_settings.collaboration.title')"
          :sub-title="$t('course_settings.collaboration.sub_title')"
          icon="person_admin"
          force-open
        >
          <CourseSettingsCollaboration
            v-if="data.course.permissions.canChangeCourseSharingSettings"
            :course="data.course"
            :read-only="!data.course.permissions.canChangeCourseSharingSettings"
          />
          <CourseSettingsCollaboration v-else :course="data.course" read-only />
        </TSettingsBlock>
        <TSettingsBlock
          v-if="data.course.permissions.canCloseCourse && !data.course.closedAt"
          :title="
            isTemplate()
              ? $t('course_settings.close_title_template')
              : $t('course_settings.close_title')
          "
          :sub-title="isTemplate() ? '' : $t('course_settings.close_sub_title')"
          icon="trash"
          force-open
        >
          <TPCaption>
            {{
              isTemplate()
                ? $t('course_settings.close_description_template')
                : $t('course_settings.close_description')
            }}
          </TPCaption>
          <TButtonInlineDangerous
            class="mt-2"
            :action="startCloseCourse"
            data-test-name="closeCourse"
          >
            {{
              isTemplate()
                ? $t('course_settings.close_button_template')
                : $t('course_settings.close_button')
            }}
          </TButtonInlineDangerous>
          <TAlertDangerous
            :open="showCloseCourseModal"
            :confirm-action-label="$t('course_settings.close_button')"
            :title="
              $t('course_settings.close_warning', [
                {
                  id: 'courseName',
                  value: data.course.name,
                },
              ])
            "
            :description="$t('course_settings.close_description')"
            :confirm-action="doCloseCourse"
            :cancel-action="cancelCloseCourse"
            :loading="closeCourseLoading"
            data-test-name="closeCourse"
          />
        </TSettingsBlock>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@urql/vue';
import { useRoute } from 'vue-router';
import { AiSettingsDocument, CourseDocument, UsageType } from '~/graphql';
import { useCourseManagement } from '~/stores/courseManagement';
import { computed, ref } from 'vue';
import toast from '~/components/Toast';
import CourseTabNavigation from '../../../components/Course/CourseTabNavigation.vue';
import { TH1, TStickyHeader } from '@teachino/shared-design-system';
import { LegacyApiCourseBadge } from '@teachino/shared-legacy-api';

definePageMeta({ layout: 'new-navigation-layout', middleware: ['is-teacher'] });
const { $t } = useNuxtApp();

const courseManagementStore = useCourseManagement();
courseManagementStore.init();

const route = useRoute();
const courseId = route.params.courseid;

const { fetching, data, error } = useQuery({
  query: CourseDocument,
  variables: {
    courseIdInput: {
      id: courseId,
    },
  },
});

const backLink = route.query.backLink
  ? `/${route.query.backLink}`
  : `/course/${courseId}/content`;

const pageTitle = computed(() => {
  if (data.value?.course) {
    return data.value?.course?.name;
  } else {
    return '';
  }
});

useHead({
  title: pageTitle.value,
});

const { data: aiSettingsData, fetching: fetchingAiSettings } = useQuery({
  query: AiSettingsDocument,
  variables: {
    courseIdInput: {
      id: courseId,
    },
  },
  requestPolicy: 'network-only',
});

const showCloseCourseModal = ref(false);
const closeCourseLoading = ref(false);

function startCloseCourse() {
  showCloseCourseModal.value = true;
}

function cancelCloseCourse() {
  showCloseCourseModal.value = false;
}

async function doCloseCourse() {
  try {
    closeCourseLoading.value = true;
    await courseManagementStore.closeCourse(courseId);
    navigateTo('/course');
    toast.success({
      header: $t('course_settings.close.success_title'),
      description: $t('course_settings.close.success_description', [
        {
          id: 'courseName',
          value: data.value?.course?.name,
        },
      ]),
    });
  } catch (e) {
    toast.error($t('generic_error'));
  }
  closeCourseLoading.value = false;
  showCloseCourseModal.value = false;
}

function isTemplate() {
  return !!(data.value && data.value.course.usage === UsageType.Template);
}
</script>
